import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { meHandler } from "src/store/reducers/meSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import setAuthDataInLocalStorage from "src/utils/auth/LocalStorage";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import VERIFY_CODE from "src/graphql/mutation/auth/verifyCode";
import RESEND_CODE from "src/graphql/mutation/auth/resendCode";
import USEREMAILREGISTERED from "src/graphql/mutation/auth/userEmailRegistered";
import useStyles from "./Styles";

interface Props {
  userEmail: null | string; // pass the email to show user where code was sent
  onClose: () => void;
  changeModalType: (a: string) => void;
  setIsVerifying: any;
}

const VerificationCodeBody: React.FC<Props> = ({
  userEmail,
  onClose,
  changeModalType,
  setIsVerifying,
}) => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  //
  const { me }: any = useSelector((state: any) => state.me);

  const { email: savedUserEmail, agentId } = useActiveUserDetail();

  // timer state
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(59);

  const [
    verifyCode,
    {
      loading: verifyCodeMutationLoading,
      error: verifyCodeMutationError,
      data: verifyCodeMutationResult,
    },
  ] = useMutation(VERIFY_CODE);

  const [
    resendCode,
    { data: resendCodeMutationResult, loading: resendCodeMutationLoading },
  ] = useMutation(RESEND_CODE);

  const handleFormikSubmit = async (
    FormValues: { code: string },
    formikHelpers: any,
  ) => {
    try {
      const { code } = FormValues;
      setIsVerifying(true);
      verifyCode({
        variables: {
          email: userEmail || savedUserEmail,
          randomCode: String(code),
        },
      });
    } catch (error) {
      formikHelpers.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema: Yup.object().shape({
      code: Yup.string()
        .matches(/^\d{6}$/, "Enter a valid 6-digit code")
        .required("Verification code is required"),
    }),
    onSubmit: handleFormikSubmit,
  });

  // handle verify result
  useEffect(() => {
    if (verifyCodeMutationResult?.verifyCode) {
      const verifiedUser = verifyCodeMutationResult.verifyCode;
      enqueueSnackbar("User has been successfully verified", {
        variant: "success",
      });
      console.log("me_before_verify", userEmail || savedUserEmail, me);
      //
      // updating ME data and localStorage
      const updatedMe = {
        ...me,
        ...verifiedUser,
      };
      setAuthDataInLocalStorage(updatedMe);
      dispatch(meHandler(updatedMe));
      //
      changeModalType("");
      setIsVerifying(true);
      onClose();
    }
  }, [verifyCodeMutationResult]);

  useEffect(() => {
    if (verifyCodeMutationError) {
      if (
        verifyCodeMutationError.message === "Failed to fetch" ||
        verifyCodeMutationError.message ===
          "An unexpected error occurred. Please try again later."
      ) {
        enqueueSnackbar("Something went wrong. Try again.", {
          variant: "error",
        });
        return;
      }

      let errorMsg = "";
      try {
        const backendErrors = JSON.parse(verifyCodeMutationError.message);

        if (typeof backendErrors === "object" && backendErrors !== null) {
          const firstKey = Object.keys(backendErrors)[0];
          errorMsg = backendErrors[firstKey] || verifyCodeMutationError.message;
        } else {
          errorMsg = String(backendErrors);
        }
      } catch {
        errorMsg = verifyCodeMutationError.message;
      }

      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  }, [verifyCodeMutationError]);

  // handle resend result
  useEffect(() => {
    if (resendCodeMutationResult?.resendCode) {
      if (!resendCodeMutationResult.resendCode.success) {
        enqueueSnackbar(resendCodeMutationResult.resendCode.error, {
          variant: "error",
        });
      } else {
        setMinutes(0);
        setSeconds(59);
        enqueueSnackbar("Code sent successfully", { variant: "success" });
      }
    }
  }, [resendCodeMutationResult]);

  // countdown timer
  useEffect(() => {
    const myInterval = setInterval(() => {
      if (seconds > 0) {
        setSeconds((prev) => prev - 1);
      }
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(myInterval);
        } else {
          setMinutes((prev) => prev - 1);
          setSeconds(59);
        }
      }
    }, 1000);
    return () => clearInterval(myInterval);
  }, [seconds, minutes]);

  const resendCodeHandler = async () => {
    try {
      await resendCode({
        variables: { email: userEmail || savedUserEmail },
      });
    } catch (err) {
      enqueueSnackbar("Something went wrong. Try again.", { variant: "error" });
    }
  };

  // -------------------------------------
  const [
    userEmailRegistered,
    {
      data: userEmailRegisteredResult,
      error: userEmailRegisteredError,
      loading: userEmailRegisteredLoading,
    },
  ] = useMutation(USEREMAILREGISTERED);

  const handleReEnterEmail = () => {
    userEmailRegistered({
      variables: {
        email: "nullify",
        agentId,
      },
    });
    // // updating ME data
    // dispatch(
    //   meHandler({
    //     email: null,
    //   }),
    // );
    // changeModalType("email");
  };

  useEffect(() => {
    if (
      userEmailRegisteredResult &&
      userEmailRegisteredResult.userEmailRegistered
    ) {
      const { success, result, error } =
        userEmailRegisteredResult.userEmailRegistered;
      const parsedResult = JSON.parse(result);
      if (success) {
        enqueueSnackbar(parsedResult.message, {
          variant: "success",
        });
        // updating ME data
        dispatch(
          meHandler({
            ...me,
            email: null,
          }),
        );
        changeModalType("email");
      } else {
        enqueueSnackbar(error, {
          variant: "error",
        });
      }
    }
  }, [userEmailRegisteredResult]);

  useEffect(() => {
    if (userEmailRegisteredError) {
      enqueueSnackbar(userEmailRegisteredError.message, {
        variant: "error",
      });
    }
  }, [userEmailRegisteredError]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="textSecondary">
          We sent a 6-digit verification code to{" "}
          <b>{userEmail || savedUserEmail}</b>. Please enter it below.
        </Typography>

        <TextField
          label="Verification Code"
          name="code"
          value={formik.values.code}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
          fullWidth
          inputProps={{ maxLength: 6 }}
        />

        <Box sx={{ display: "flex", gap: "5px", mt: "20px" }}>
          <Button
            sx={{ width: "100%", textTransform: "capitalize" }}
            variant="contained"
            color="secondary"
            onClick={handleReEnterEmail}
            disabled={userEmailRegisteredLoading}
            endIcon={
              userEmailRegisteredLoading && (
                <CircularProgress size={15} sx={{ color: "white" }} />
              )
            }
          >
            Re-enter Email
          </Button>

          <Button
            sx={{ width: "100%", textTransform: "capitalize" }}
            disabled={verifyCodeMutationLoading}
            type="submit"
            variant="contained"
            color="primary"
            className={classes.saveButton}
            endIcon={
              verifyCodeMutationLoading && (
                <CircularProgress size={15} sx={{ color: "white" }} />
              )
            }
          >
            Verify Code
          </Button>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
          <Typography variant="body2">
            Didn't receive the code?{" "}
            {seconds > 0 ? (
              <span>
                You can resend in {minutes}:
                {seconds < 10 ? `0${seconds}` : seconds}
              </span>
            ) : (
              <span>You can resend now</span>
            )}
          </Typography>
          <Button
            sx={{ mt: 1 }}
            disabled={seconds !== 0 || resendCodeMutationLoading}
            onClick={resendCodeHandler}
            variant="text"
          >
            {resendCodeMutationLoading ? (
              <CircularProgress size={18} />
            ) : (
              "Resend Code"
            )}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default VerificationCodeBody;
